import React, { useEffect, useState, useCallback } from "react";
import { Tabs, Tab, Box, Typography, Container, Alert } from "@mui/material";
import { useLocation } from "react-router-dom";
import CandidateCard from "../../CandidateCard";
import PaginatedListLayout from "../../../../shared/pagination/PaginatedListLayout";

// タブ定義
const TABS = [
  { label: "プロジェクト", value: "project" },
  { label: "転職先", value: "job" },
];

const CandidateListPage = () => {
  const [tabIndex, setTabIndex] = useState(0); // 0: プロジェクト, 1: 転職先
  const [currentPage, setCurrentPage] = useState(1);
  const [candidates, setCandidates] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const location = useLocation();
  const [flashMessage, setFlashMessage] = useState(null);

  const fetchCandidates = useCallback(async (page = 1, nextTabIndex = 0) => {
    setLoading(true);
    setErrorMessage(null);

    const status = TABS[nextTabIndex]?.value || TABS[0].value;

    try {
      const res = await fetch(
        `/company/candidates.json?status=${status}&page=${page}`,
      );

      if (!res.ok) throw new Error("人材一覧の取得に失敗しました。");

      const data = await res.json();
      setCandidates(data.users ?? []);
      setPagination(data.pagination ?? null);

      if (data.pagination && typeof data.pagination.current_page === "number") {
        const serverPage = data.pagination.current_page;

        // 今回要求したページとサーバー返却ページが違うときだけ同期
        if (serverPage !== page) {
          setCurrentPage(serverPage);
        }
      }
    } catch (error) {
      console.error("人材一覧の取得に失敗しました", error);
      setCandidates([]);
      setPagination(null);
      setErrorMessage("人材一覧の取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCandidates(currentPage, tabIndex);
  }, [tabIndex, location.key, fetchCandidates]);

  useEffect(() => {
    // SPA遷移時はlocation.stateで拾う
    if (location.state?.flashMessage) {
      setFlashMessage(location.state.flashMessage);
      // 履歴クリアも忘れずに
      window.history.replaceState({}, document.title, location.pathname);
      return;
    }

    // SSR/リロード時はdata属性から拾う
    const root = document.querySelector(".react-app-router");
    if (root && root.dataset.flashMessage) {
      try {
        setFlashMessage(JSON.parse(root.dataset.flashMessage));
        // data属性消しておくと安全（同じメッセージが何度も表示されるのを防ぐため）
        root.dataset.flashMessage = "";
      } catch (e) {
        setFlashMessage(root.dataset.flashMessage);
        root.dataset.flashMessage = "";
      }
    }
  }, [location.key, location.state, location.pathname]);

  // ページ遷移やタブ切り替えのたびにスクロール位置を最上部にリセット
  const scrollToListTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "auto",
    });
  }, []);

  // タブ切り替え時の処理
  const handleTabChange = (_, newTabIndex) => {
    setTabIndex(newTabIndex);
    setCurrentPage(1);

    scrollToListTop();
  };

  // ページ切り替え時の処理
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchCandidates(page, tabIndex);
    scrollToListTop();
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        mt: 2,
        mb: 2,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* フラッシュメッセージ (固定) */}
      {flashMessage && (
        <Box sx={{ flexShrink: 0 }}>
          <Alert
            severity={
              typeof flashMessage === "object"
                ? flashMessage.type || "success"
                : "success"
            }
          >
            {typeof flashMessage === "object"
              ? flashMessage.message
              : flashMessage}
          </Alert>
        </Box>
      )}

      {/* 固定ヘッダー領域 (Tabsまで) */}
      <Box
        sx={{
          position: "sticky",
          top: "63px",
          zIndex: 20,
          backgroundColor: "#F4F7FA",
          boxShadow: 0,
          mx: -2,
          px: 2,
          pt: 1.5,
        }}
      >
        <Typography variant="h5" fontWeight="bold" mb={2}>
          人材一覧
        </Typography>

        <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mb: 2 }}>
          {TABS.map((tab) => (
            <Tab key={tab.value} label={tab.label} />
          ))}
        </Tabs>

        {errorMessage && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        )}
      </Box>

      {/* 一覧エリア */}
      <Box
        sx={{
          flexGrow: 1,
          pr: 1,
          pb: 10,
        }}
      >
        <PaginatedListLayout
          items={candidates}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          emptyText="該当する人材はありません。"
        >
          <Box display="flex" flexDirection="column" gap={2}>
            {candidates.map((candidate) => (
              <CandidateCard
                key={candidate.id}
                candidate={candidate}
                tabType={TABS[tabIndex]?.value}
              />
            ))}
          </Box>
        </PaginatedListLayout>
      </Box>
    </Container>
  );
};

export default CandidateListPage;
